import { z } from 'zod';
import DOMPurify from 'dompurify';
import type { PropertyType } from '@shared/lib';

// ============================================
// EMAIL UNIQUENESS
// ============================================

/**
 * Error thrown when a pipeline entry with the same email already exists.
 * Carries the conflicting email and the existing entry's ID for UI display.
 */
export class DuplicateEmailError extends Error {
  constructor(
    public readonly email: string,
    public readonly existingEntryId: string
  ) {
    super(`Ein Eintrag mit der E-Mail "${email}" existiert bereits.`);
    this.name = 'DuplicateEmailError';
  }
}

/**
 * Normalize an email for use as a Firestore document ID.
 * Lowercase + trim — matches the emailSchema transform.
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Property option for select/multiselect fields
 */
interface PropertyOption {
  value: string;
  label: string;
}

/**
 * Minimal property definition for validation
 * Compatible with Property entity
 */
interface ValidationProperty {
  name: string;
  label: string;
  type: PropertyType;
  required?: boolean;
  options?: PropertyOption[];
}

/**
 * Field change for audit log
 */
export interface FieldChange {
  field: string;
  fieldLabel: string;
  oldValue: unknown;
  newValue: unknown;
}

// ============================================
// ZOD SCHEMAS for Input Validation
// ============================================

/**
 * Email address validation schema
 */
export const emailSchema = z
  .string()
  .email('Ungültige E-Mail-Adresse')
  .min(1, 'E-Mail ist erforderlich')
  .max(254, 'E-Mail darf max. 254 Zeichen haben')
  .transform((email) => email.toLowerCase().trim());

/**
 * Validate email address
 * @returns null if valid, error message if invalid
 */
export function validateEmail(email: string): string | null {
  const result = emailSchema.safeParse(email);
  if (!result.success) {
    return result.error.issues[0]?.message ?? 'Ungültige E-Mail-Adresse';
  }
  return null;
}

/**
 * Sanitize string to prevent XSS — strips all HTML tags and attributes
 */
export function sanitizeString(input: string): string {
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

/**
 * Sanitize HTML content (for rich text/email display)
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html);
}

/**
 * Validation error for a specific field
 */
export interface FieldValidationError {
  field: string;
  message: string;
}

/**
 * Check if a value is considered "empty" for validation purposes
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Validate a single field value against its property definition
 *
 * @param value - The value to validate
 * @param property - The property definition
 * @returns Error message if invalid, null if valid
 */
export function validateField(value: unknown, property: ValidationProperty): string | null {
  // Check required
  if (property.required && isEmpty(value)) {
    return `${property.label} ist ein Pflichtfeld`;
  }

  // Type-specific validation could be added here
  // e.g., email format, number ranges, etc.

  return null;
}

/**
 * Validate all required fields in a properties object
 *
 * @param properties - The properties object to validate
 * @param propertyDefinitions - All property definitions
 * @returns Array of validation errors (empty if all valid)
 */
export function validateRequiredFields(
  properties: Record<string, unknown>,
  propertyDefinitions: ValidationProperty[]
): FieldValidationError[] {
  const errors: FieldValidationError[] = [];

  for (const prop of propertyDefinitions) {
    if (prop.required) {
      const value = properties[prop.name];
      const error = validateField(value, prop);
      if (error) {
        errors.push({
          field: prop.name,
          message: error,
        });
      }
    }
  }

  return errors;
}

/**
 * Check if a property is a required field
 */
export function isRequiredField(property: ValidationProperty): boolean {
  return property.required === true;
}

/**
 * Get all required properties from a list of properties
 */
export function getRequiredProperties<T extends ValidationProperty>(properties: T[]): T[] {
  return properties.filter((p) => p.required);
}

/**
 * Compare two values for equality (handles arrays, objects, etc.)
 */
function valuesAreEqual(oldValue: unknown, newValue: unknown): boolean {
  // Handle null/undefined
  if (oldValue === null || oldValue === undefined) {
    return newValue === null || newValue === undefined;
  }
  if (newValue === null || newValue === undefined) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    if (oldValue.length !== newValue.length) return false;
    return oldValue.every((v, i) => v === newValue[i]);
  }

  // Handle Firestore Timestamps (check for seconds property)
  if (
    typeof oldValue === 'object' &&
    'seconds' in oldValue &&
    typeof newValue === 'object' &&
    'seconds' in newValue
  ) {
    return (oldValue as { seconds: number }).seconds === (newValue as { seconds: number }).seconds;
  }

  // Simple equality
  return oldValue === newValue;
}

/**
 * Detect changes between old and new property values
 *
 * @param oldProperties - The current/old property values
 * @param newProperties - The new property values being set
 * @param propertyDefinitions - Property definitions (for labels)
 * @returns Array of field changes (empty if no changes)
 */
export function detectChanges(
  oldProperties: Record<string, unknown>,
  newProperties: Record<string, unknown>,
  propertyDefinitions: ValidationProperty[]
): FieldChange[] {
  const changes: FieldChange[] = [];

  // Build a map of property name to label
  const labelMap = new Map(propertyDefinitions.map((p) => [p.name, p.label]));

  // Check each new property value
  for (const [field, newValue] of Object.entries(newProperties)) {
    const oldValue = oldProperties[field];

    // Skip if values are equal
    if (valuesAreEqual(oldValue, newValue)) {
      continue;
    }

    changes.push({
      field,
      fieldLabel: labelMap.get(field) ?? field,
      oldValue: oldValue ?? null, // Firestore doesn't accept undefined
      newValue: newValue ?? null, // Firestore doesn't accept undefined
    });
  }

  return changes;
}
